import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { camelizeKeys } from './wire-format';
import { environment } from 'src/environments/environment';

export interface AiProvider {
  id: number;
  name: string;
  providerType: string;
  baseUrl?: string | null;
  /** Key Vault secret NAME — preferred in production. */
  apiKeySecretName?: string | null;
  /** Write-only. Sent when an admin enters a key; never returned by the API. */
  apiKey?: string | null;
  /** Whether a key is already stored. The key itself is never sent to the browser. */
  hasApiKey?: boolean;
  isEnabled: boolean;
}

export interface AiModel {
  id: number;
  providerId: number;
  providerName?: string;
  modelKey: string;
  displayName: string;
  supportsToolCalling: boolean;
  supportsStreaming: boolean;
  supportsVision: boolean;
  supportsTemperature: boolean;
  supportsEffort: boolean;
  supportsThinking: boolean;
  contextWindow: number;
  maxOutputTokens: number;
  isEnabled: boolean;
}

export interface AiAssignment {
  purpose: string;
  modelId: number;
  fallbackModelId?: number | null;
  modelDisplayName?: string;
}

export interface AiConnectionTestResult {
  success: boolean;
  message: string;
  durationMs: number;
}

export interface AiToolInfo {
  name: string;
  description?: string | null;
  /** False when the tool is switched off in configuration for every role. */
  isEnabledInCode: boolean;
  /** True for anything that can change data. */
  isWrite: boolean;
}

export interface AiRoleInfo {
  id: number;
  name: string;
}

export interface AiToolPermission {
  toolName: string;
  roleId: number;
  isEnabled: boolean;
}

/**
 * The tool/role grid.
 *
 * `permissions` holds only the pairs that have been explicitly decided — a tool with no entry for a
 * role is available to it. The grid therefore has to be drawn from `tools` x `roles`, not from
 * `permissions`, or a deployment that has never been configured would render as empty when in fact
 * everything is switched on.
 */
export interface AiToolPermissionMatrix {
  tools: AiToolInfo[];
  roles: AiRoleInfo[];
  permissions: AiToolPermission[];
}

@Injectable({ providedIn: 'root' })
export class AiAdminService {
  private readonly baseUrl = `${environment.APIUrl}/Api/AiAdmin`;

  constructor(private readonly http: HttpClient) {}

  getProviderTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}/ProviderTypes`);
  }

  getProviders(): Observable<AiProvider[]> {
    return this.http
      .get<AiProvider[]>(`${this.baseUrl}/Providers`)
      .pipe(map(camelizeKeys<AiProvider[]>));
  }

  saveProvider(provider: AiProvider): Observable<AiProvider> {
    return this.http
      .post<AiProvider>(`${this.baseUrl}/Providers`, provider)
      .pipe(map(camelizeKeys<AiProvider>));
  }

  deleteProvider(providerId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Providers/${providerId}`);
  }

  getModels(): Observable<AiModel[]> {
    return this.http
      .get<AiModel[]>(`${this.baseUrl}/Models`)
      .pipe(map(camelizeKeys<AiModel[]>));
  }

  saveModel(model: AiModel): Observable<AiModel> {
    return this.http
      .post<AiModel>(`${this.baseUrl}/Models`, model)
      .pipe(map(camelizeKeys<AiModel>));
  }

  deleteModel(modelId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/Models/${modelId}`);
  }

  getAssignments(): Observable<AiAssignment[]> {
    return this.http
      .get<AiAssignment[]>(`${this.baseUrl}/Assignments`)
      .pipe(map(camelizeKeys<AiAssignment[]>));
  }

  setAssignment(assignment: AiAssignment): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/Assignments`, assignment);
  }

  testConnection(modelId: number): Observable<AiConnectionTestResult> {
    return this.http
      .post<AiConnectionTestResult>(`${this.baseUrl}/Models/${modelId}/TestConnection`, {})
      .pipe(map(camelizeKeys<AiConnectionTestResult>));
  }

  getToolPermissions(): Observable<AiToolPermissionMatrix> {
    return this.http
      .get<AiToolPermissionMatrix>(`${this.baseUrl}/ToolPermissions`)
      .pipe(map(camelizeKeys<AiToolPermissionMatrix>));
  }

  saveToolPermissions(permissions: AiToolPermission[]): Observable<AiToolPermissionMatrix> {
    return this.http
      .put<AiToolPermissionMatrix>(`${this.baseUrl}/ToolPermissions`, { permissions })
      .pipe(map(camelizeKeys<AiToolPermissionMatrix>));
  }
}

/**
 * Which generation controls each provider actually accepts.
 *
 * This drives the admin form so an administrator is not offered a setting the provider will
 * reject. Anthropic's current models return HTTP 400 for temperature/top_p/top_k and use an
 * effort level instead, so a single shared "temperature" field would break every Anthropic model
 * the moment it was used. The server validates the same rules independently — this is here to
 * keep the form honest, not to be the enforcement.
 */
export const PROVIDER_CAPABILITY_HINTS: Record<
  string,
  { temperature: boolean; effort: boolean; thinking: boolean; note?: string }
> = {
  Anthropic: {
    temperature: false,
    effort: true,
    thinking: true,
    note: 'Anthropic models reject temperature. Use effort (low…max) to control reasoning depth.'
  },
  OpenAI: { temperature: true, effort: false, thinking: false },
  Google: { temperature: true, effort: false, thinking: false },
  Moonshot: { temperature: true, effort: false, thinking: false },
  AzureOpenAI: {
    temperature: true,
    effort: false,
    thinking: false,

    // The two things that make an Azure provider fail in ways the error message does not explain:
    // the endpoint is per-resource, and requests route to a deployment rather than a model name.
    note:
      'Azure routes to a deployment, so the model key must be your deployment name — not the ' +
      'underlying model. Set the endpoint to https://{your-resource}.openai.azure.com. Append ' +
      '?api-version=… only if your resource needs a version other than the default.'
  }
};
